import { moduleCount as countModules, personaOf } from '@titan/shared';
import { ChevronLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, Outlet, useNavigate } from 'react-router';
import { useSession, type MeResponse } from '../session/SessionProvider.js';
import { Avatar, Button, Eyebrow } from '../ui/index.js';
import { Sidebar } from './Sidebar.js';
import styles from './Shell.module.css';

const cx = (...parts: Array<string | false | undefined | null>) => parts.filter(Boolean).join(' ');

function personaRole(me: MeResponse): string {
  const actor = me.actor;
  const persona = personaOf(actor);
  if (persona.kind === 'staff') return persona.role === 'superadmin' ? 'Platform Owner · DinoCloud' : 'DinoCloud staff';
  const role = persona.isSuperadmin ? 'Superadmin' : persona.levels.management === 'full' ? 'Administrator' : 'Member';
  return `${role} at ${persona.tenant.name}`;
}

export function TopBar({ me }: { me: MeResponse }) {
  const persona = personaOf(me.actor);
  return (
    <header className={styles.topbar}>
      <Link to="/" className={styles.wordmark} aria-label="Titan home">
        <span className={styles.wordmarkTitle}>Titan</span>
        <span className={styles.wordmarkSub}>by DinoCloud</span>
      </Link>
      <div className={styles.persona}>
        {persona.kind === 'tenant' && <span className={styles.tenant}>{persona.tenant.name}</span>}
        <div className={styles.personaText}>
          <span className={styles.personaName}>{persona.name}</span>
          <span className={styles.personaRole}>{personaRole(me)}</span>
        </div>
        <Avatar name={persona.name} />
      </div>
    </header>
  );
}

/** Spec 09. Sticky, dark, warm-accented, not dismissible without exiting. */
export function ImpersonationBanner({ me }: { me: MeResponse }) {
  const { exitImpersonation } = useSession();
  const navigate = useNavigate();
  const imp = me.impersonation;
  if (!imp) return null;

  const modules = me.actor.kind === 'staff' && me.actor.impersonating ? countModules({ modules: me.actor.impersonating.persona.tenant.entitlements }) : imp.moduleCount;
  const meta = imp.blocked
    ? `${imp.plan ?? ''} plan · nobody at this tenant currently has access`
    : `${imp.plan ?? ''} plan · ${modules} of 6 modules · acting as ${imp.persona.name} · every action is logged against your DinoCloud account`;

  const exit = async () => {
    const tenantId = await exitImpersonation();
    navigate(tenantId ? `/admin/tenants/${tenantId}` : '/admin/tenants');
  };

  return (
    <div className={styles.banner} role="status">
      <span className={styles.badge}>Impersonating</span>
      <div className={styles.bannerText}>
        <span className={styles.bannerTitle}>You are seeing the hub as {imp.tenantName}</span>
        <span className={styles.bannerMeta}>{meta}</span>
      </div>
      <button type="button" className={styles.bannerExit} onClick={exit}>
        Exit impersonation
      </button>
    </div>
  );
}

/** Spec 09 blocked state: a session that cannot exist is not rendered. */
export function BlockedScreen({ me }: { me: MeResponse }) {
  const { exitImpersonation } = useSession();
  const navigate = useNavigate();
  const blocked = me.impersonation?.blocked;
  if (!blocked) return null;
  const suspended = blocked.reason === 'suspended';
  const exit = async () => {
    const tenantId = await exitImpersonation();
    navigate(tenantId ? `/admin/tenants/${tenantId}` : '/admin/tenants');
  };
  return (
    <div className={styles.blockedWrap}>
      <div className={styles.blocked}>
        <h1 className={styles.blockedTitle}>{suspended ? 'This tenant is suspended' : 'This tenant has nobody with access yet'}</h1>
        <p className={styles.blockedBody}>
          {suspended
            ? 'Suspending a tenant revokes every user’s access, so there is nothing a client could see here. Reactivate the tenant from its detail screen, then impersonate it again.'
            : 'Every user at this tenant is still on an unaccepted invitation, so no session exists to impersonate. Impersonation becomes available as soon as somebody signs in for the first time.'}
        </p>
        <div>
          <Button onClick={exit}>Exit impersonation</Button>
        </div>
      </div>
    </div>
  );
}

/** Banner, sidebar, top bar and the screen outlet. */
export function Shell() {
  const { me } = useSession();
  if (!me) return null;
  const blocked = Boolean(me.impersonation?.blocked);
  return (
    <div className={styles.shell}>
      <ImpersonationBanner me={me} />
      <div className={styles.body}>
        <Sidebar nav={me.nav} />
        <div className={styles.main}>
          <TopBar me={me} />
          {blocked ? <BlockedScreen me={me} /> : <Outlet />}
        </div>
      </div>
    </div>
  );
}

export interface ScreenHeaderProps {
  eyebrow: string;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  back?: { label: string; to: string } | { label: string; onClick: () => void };
  /** Detail screens: tighter padding, 30px title. */
  detail?: boolean;
  /** Pills rendered in a row beside the title. */
  titleAside?: ReactNode;
  /** Extra lines under the title (meta lines on detail screens). */
  meta?: ReactNode;
}

/** Spec 00 header band. Eyebrow text is lowercase in source. */
export function ScreenHeader({ eyebrow, title, description, actions, back, detail, titleAside, meta }: ScreenHeaderProps) {
  const navigate = useNavigate();
  return (
    <div className={cx(styles.band, detail && styles.bandDetail)}>
      <div className={styles.bandInner}>
        <div className={styles.bandText}>
          {back && (
            <button type="button" className={styles.back} onClick={() => ('to' in back ? navigate(back.to) : back.onClick())}>
              <ChevronLeft size={15} strokeWidth={1.75} aria-hidden="true" />
              {back.label}
            </button>
          )}
          <Eyebrow>{eyebrow}</Eyebrow>
          <div className={styles.titleRow}>
            <h1 className={cx(styles.title, detail && styles.titleDetail)}>{title}</h1>
            {titleAside}
          </div>
          {description && <p className={styles.description}>{description}</p>}
          {meta}
        </div>
        {actions && <div className={styles.bandActions}>{actions}</div>}
      </div>
    </div>
  );
}

export function ScreenBody({ narrow, children }: { narrow?: boolean; children: ReactNode }) {
  return <main className={cx(styles.screenBody, narrow && styles.narrow, 'screen-enter')}>{children}</main>;
}

export function Loading({ children = 'Loading…' }: { children?: ReactNode }) {
  return <div className={styles.loading}>{children}</div>;
}
